class ProductDetailsBlock extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    // Store references to event listeners for cleanup
    this.boundVariantChangeHandler = this.handleVariantChange.bind(this);
    this.boundClickHandler = this.handleClick.bind(this);
    this.boundCustomVariantChangeHandler = this.handleCustomVariantChange.bind(this);
  }

  setupEventListeners() {
    const variantSelectors = document.querySelectorAll('[name="id"], .variant-input, variant-picker, variant-picker select');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.boundVariantChangeHandler);
    });

    document.addEventListener('variant:change', this.boundCustomVariantChangeHandler);

    document.addEventListener('click', this.boundClickHandler);
  }

  removeEventListeners() {
    const variantSelectors = document.querySelectorAll('[name="id"], .variant-input, variant-picker, variant-picker select');
    variantSelectors.forEach(selector => {
      selector.removeEventListener('change', this.boundVariantChangeHandler);
    });
    document.removeEventListener('variant:change', this.boundCustomVariantChangeHandler);
    document.removeEventListener('click', this.boundClickHandler);
  }

  /**
   * @param {Event} event
   */
  handleVariantChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    const variantId = target?.value;
    if (variantId) {
      this.updateProductDetails(variantId);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  handleCustomVariantChange(event) {
    if (event.detail && event.detail.variant) {
      this.updateProductDetails(event.detail.variant.id);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target && typeof target.closest === 'function') {
      const variantElement = target.closest('[data-variant-id]');
      if (variantElement) {
        const variantId = variantElement.getAttribute('data-variant-id');
        if (variantId) {
          this.updateProductDetails(variantId);
        }
      }
    }
  }

  /**
   * @param {string} variantId
   */
  updateProductDetails(variantId) {
    // Dispatch variant change event
    this.dispatchEvent(new CustomEvent('product-details:variant-change', {
      detail: { variantId },
      bubbles: true
    }));

    // Get variant data from the page
    const variantDataElement = document.querySelector('[data-variant-data]');
    if (!variantDataElement) return;

    try {
      const variantDataText = variantDataElement.textContent;
      if (!variantDataText) return;
      
      const allVariants = JSON.parse(variantDataText);
      const currentVariant = allVariants.find(function(/** @type {any} */ v) { 
        return v.id == variantId; 
      });

      if (currentVariant) {
        const fallbackFabricVariant = allVariants.find(function(/** @type {any} */ v) { 
          return v.metafields && 
          v.metafields.custom && 
          v.metafields.custom.fabric && 
          v.metafields.custom.fabric.value;
        });

        // Create variant object with fallback logic
        const variantWithFallback = {
          ...currentVariant,
          metafields: {
            ...currentVariant.metafields,
            custom: {
              ...currentVariant.metafields?.custom,
              fabric: {
                ...currentVariant.metafields?.custom?.fabric,
                // Use current variant's fabric data if available, otherwise use fallback
                value: (currentVariant.metafields?.custom?.fabric?.value) || 
                       (fallbackFabricVariant?.metafields?.custom?.fabric?.value)
              }
            }
          }
        };

        this.updateContent(variantWithFallback);
      }
    } catch (error) {
      // Silent error handling
      console.warn('Error updating product details:', error);
    }
  }

  /**
   * @param {any} variant
   */
  updateContent(variant) {
    try {
      // Dispatch before update event
      this.dispatchEvent(new CustomEvent('product-details:before-update', {
        detail: { variant },
        bubbles: true
      }));

      // Add loading state for screen readers
      this.setAttribute('aria-busy', 'true');

      let newContent = '';

      // Add fabric metafields if they exist and block is configured to show them
      const showFabric = this.hasAttribute('data-show-fabric');
      if (showFabric && variant.metafields && variant.metafields.custom && variant.metafields.custom.fabric) {
        const fabricValue = variant.metafields.custom.fabric.value;
        if (fabricValue) {
          const fabricLabel = this.getAttribute('data-fabric-label');
          const fabricFormat = this.getAttribute('data-fabric-format') || 'list';

          newContent += '<div class="product-details-block__fabric">';
          if (fabricLabel) {
            newContent += `<p class="product-details-block__fabric-label">${fabricLabel}:</p>`;
          }

          if (fabricValue && fabricValue !== null) {
            let fabricItems = [];

            // Handle different fabric data types
            if (Array.isArray(fabricValue)) {
              fabricItems = fabricValue;
            } else if (typeof fabricValue === 'string') {
              try { // Try to parse as JSON first
                const parsed = JSON.parse(fabricValue);
                if (Array.isArray(parsed)) {
                  fabricItems = parsed;
                } else { // Single string value
                  fabricItems = [fabricValue];
                }
              } catch (e) { // Not JSON, treat as comma-separated string
                fabricItems = fabricValue.split(',').map(item => item.trim()).filter(item => item.length > 0);
              }
            } else { // Fallback - treat as single item
              fabricItems = [String(fabricValue)];
            }

            // Generate the fabric content
            if (fabricItems.length > 0) {
              if (fabricFormat === 'list') {
                newContent += '<ul class="product-details-block__fabric-list">';
                fabricItems.forEach(item => {
                  newContent += `<li>${item}</li>`;
                });
                newContent += '</ul>';
              } else {
                newContent += `<p class="product-details-block__fabric-text">${
                  fabricItems.join(', ')
                }</p>`;
              }
            }
          }
          newContent += '</div>';
        }
      }

      // Add weight if it exists and block is configured to show it
      const showWeight = this.hasAttribute('data-show-weight');
      if (showWeight && variant.weight && variant.weight > 0) {
        const weightLabel = this.getAttribute('data-weight-label');
        if (weightLabel) {
          newContent += `<p class="product-details-block__weight">${weightLabel}: ${variant.weight}</p>`;
        } else {
          newContent += `<p class="product-details-block__weight">${variant.weight}</p>`;
        }
      }

      const showBarcode = this.hasAttribute('data-show-barcode');
      if (showBarcode && variant.barcode) {
        const barcodeLabel = this.getAttribute('data-barcode-label');
        if (barcodeLabel) {
          newContent += `<p class="product-details-block__barcode">${barcodeLabel}: ${variant.barcode}</p>`;
        } else {
          newContent += `<p class="product-details-block__barcode">${variant.barcode}</p>`;
        }
      }

      // Add custom text (preserve static content)
      const customTextElement = this.querySelector('.product-details-block__text');
      if (customTextElement) {
        newContent += customTextElement.outerHTML;
      }

      // Add SKU at the bottom if it exists and block is configured to show it
      const showSku = this.hasAttribute('data-show-sku');
      if (showSku && variant.sku) {
        const skuLabel = this.getAttribute('data-sku-label');
        const trimmedSku = variant.sku.split('_')[0];
        if (skuLabel) {
          newContent += `<p class="product-details-block__sku">${skuLabel}: ${trimmedSku}</p>`;
        } else {
          newContent += `<p class="product-details-block__sku">${trimmedSku}</p>`;
        }
      }

      // Update the content
      this.innerHTML = newContent;

      // Remove loading state and announce update to screen readers
      this.setAttribute('aria-busy', 'false');
      this.setAttribute('aria-live', 'polite');

      // Dispatch after update event
      this.dispatchEvent(new CustomEvent('product-details:after-update', {
        detail: { variant, content: newContent },
        bubbles: true
      }));

      // Announce the update to screen readers
      const announcement = `Product details updated for variant ${variant.sku || variant.id}`;
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      liveRegion.textContent = announcement;
      document.body.appendChild(liveRegion);

      // Remove the announcement after a short delay
      setTimeout(() => {
        if (liveRegion.parentNode) {
          liveRegion.parentNode.removeChild(liveRegion);
        }
      }, 1000);

    } catch (error) {
      this.setAttribute('aria-busy', 'false');
      console.warn('Error updating product details block:', error);
    }
  }
}
customElements.define('product-details-block', ProductDetailsBlock);

// Simple accordion metafield handler
class AccordionMetafieldHandler extends HTMLElement {
  connectedCallback() {
    document.addEventListener('variant:update', this);
    this.initializeMetafields();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this);
  }

  /**
   * Initialize metafield data on page load
   */
  initializeMetafields() {
    const variantInput = document.querySelector('input[name="id"]');
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = (variantInput instanceof HTMLInputElement ? variantInput.value : null) || urlParams.get('variant') || null;
    
    if (variantId) {
      this.updateMetafields(variantId);
    } else {
      const variantData = document.querySelector('[data-variant-data]');
      if (variantData?.textContent) {
        try {
          const variants = JSON.parse(variantData.textContent);
          if (variants.length > 0) {
            this.updateMetafields(variants[0].id);
          }
        } catch (e) {
          console.warn('Error initializing metafields:', e);
        }
      }
    }
  }

  /**
   * @param {CustomEvent} event
   */
  handleEvent(event) {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    this.updateMetafields(variantId);
  }

  /**
   * Update metafields based on variant ID
   * @param {string|number} variantId
   */
  updateMetafields(variantId) {
    const variantData = document.querySelector('[data-variant-data]');
    if (!variantData?.textContent) return;

    try {
      const variants = JSON.parse(variantData.textContent);
      const currentVariant = variants.find((/** @type {any} */ v) => v.id == variantId);
      if (!currentVariant) return;

      const firstVariantForLegacy = variants[0];
      let legacyValue = firstVariantForLegacy?.metafields?.custom?.model_size?.value || '';
      if (!legacyValue) {
        legacyValue = currentVariant?.metafields?.custom?.model_size?.value || '';
      }
      document.querySelectorAll('[data-variant-metafield="model_size"]').forEach((el) => {
        if (legacyValue) {
          el.innerHTML = `<li>${legacyValue}</li>`;
        } else {
          el.innerHTML = '';
        }
      });

      /** @type {NodeListOf<HTMLElement>} */
      const metaTextNodes = document.querySelectorAll('[data-variant-metafield-namespace][data-variant-metafield-key]');
      metaTextNodes.forEach((node) => {
        const ns = node.getAttribute('data-variant-metafield-namespace');
        const key = node.getAttribute('data-variant-metafield-key');
        const prefix = node.getAttribute('data-prefix') || '';
        if (!ns || !key) return;

        let raw = null;
        
        raw = currentVariant?.metafields?.[ns]?.[key]?.value;
        if (raw == null) {
          /** @type {any} */
          const mfAll = currentVariant?.metafields_all?.[ns]?.[key];
          if (mfAll != null) {
            raw = (typeof mfAll === 'object' && mfAll !== null && 'value' in mfAll) ? mfAll.value : mfAll;
          }
        }
        
        if (raw == null || raw === '' || (Array.isArray(raw) && raw.length === 0)) {
          const firstVariant = variants[0];
          if (firstVariant) {
            raw = firstVariant?.metafields?.[ns]?.[key]?.value;
            if (raw == null) {
              /** @type {any} */
              const mfAll = firstVariant?.metafields_all?.[ns]?.[key];
              if (mfAll != null) {
                raw = (typeof mfAll === 'object' && mfAll !== null && 'value' in mfAll) ? mfAll.value : mfAll;
              }
            }
          }
        }

        let display = '';
        if (Array.isArray(raw)) {
          display = raw.join(', ');
        } else if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              display = parsed.join(', ');
            } else {
              display = raw;
            }
          } catch {
            display = raw;
          }
        } else if (raw != null) {
          display = String(raw);
        }

        const wrapper = node.closest('.accordion-metafield-text');

        if (display && display.trim() !== '') {
          if (wrapper instanceof HTMLElement) wrapper.style.display = '';
          const full = prefix ? `${prefix} ${display}` : display;
          node.textContent = full;
        } else {
          if (wrapper instanceof HTMLElement) wrapper.style.display = 'none';
          node.textContent = '';
        }
      });
    } catch (e) {
      console.warn('Error updating accordion metafield:', e);
    }
  }
}

customElements.define('accordion-metafield-handler', AccordionMetafieldHandler);